import React from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  Paper,
  Chip,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Home as HomeIcon,
  Business as BusinessIcon,
  Star as StarIcon,
  LocationOn as LocationIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Schedule as ScheduleIcon,
} from '@mui/icons-material';

const About = () => {
  const achievements = [
    "5000+ Satisfied Customers",
    "Premium Product Quality",
    "Expert Installation Services",
    "Competitive Wholesale Pricing",
    "Comprehensive Product Range"
  ];

  const services = [
    {
      icon: HomeIcon,
      title: "Residential Solutions",
      description: "Complete home renovation solutions with premium tiles, granite, and fittings"
    },
    {
      icon: BusinessIcon,
      title: "Commercial Projects",
      description: "Large-scale commercial installations for offices, hotels, and public spaces"
    },
    {
      icon: StarIcon,
      title: "Premium Quality",
      description: "Only the finest materials from trusted manufacturers and suppliers"
    }
  ];

  return (
    <Box sx={{ py: 8, backgroundColor: "#f8f9fa" }}>
      <Container maxWidth="lg">
        {/* Hero Section with Showroom Image */}
        <Box 
          sx={{ 
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
            gap: 6,
            alignItems: "center",
            mb: 8
          }}
        >
          <Box>
            <Typography
              variant="h3"
              component="h2"
              sx={{
                fontWeight: "bold",
                color: "#1a237e",
                mb: 3,
                fontSize: { xs: "2rem", md: "2.5rem" }
              }}
            >
              About Maa Saryu Agency
            </Typography>
            <Typography
              variant="h6"
              sx={{
                color: "#666",
                mb: 4,
                lineHeight: 1.7,
                fontSize: { xs: "1rem", md: "1.1rem" }
              }}
            >
              With over 15 years of experience in the industry, Maa Saryu Agency has established 
              itself as a trusted supplier of high-quality tiles, granite, CP fittings, bath fittings, 
              sanitary ware, and home décor items in Ayodhya, Uttar Pradesh.
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: "#555",
                mb: 4,
                lineHeight: 1.6
              }}
            >
              We serve both wholesale and retail customers with a commitment to excellence, 
              offering competitive pricing, expert consultation, and reliable delivery services. 
              Our extensive product range and dedication to customer satisfaction have made us 
              a preferred choice for contractors, builders, architects, and homeowners across the region.
            </Typography>
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 4 }}>
              <Chip 
                label="✓ Wholesale & Retail" 
                sx={{ backgroundColor: "#e3f2fd", color: "#1976d2" }} 
              />
              <Chip 
                label="✓ Expert Consultation" 
                sx={{ backgroundColor: "#e8f5e8", color: "#388e3c" }} 
              />
              <Chip 
                label="✓ Quality Guaranteed" 
                sx={{ backgroundColor: "#fff3e0", color: "#f57c00" }} 
              />
            </Box>
          </Box>
          <Box>
            <Paper
              elevation={8}
              sx={{
                borderRadius: "16px",
                overflow: "hidden",
                position: "relative",
                "&:hover": {
                  transform: "translateY(-4px)",
                  transition: "all 0.3s ease-in-out",
                }
              }}
            >
              <Box
                component="img"
                src="/images/showroom_image.png"
                alt="Maa Saryu Agency Showroom"
                sx={{
                  width: "100%",
                  height: { xs: "250px", md: "350px" },
                  objectFit: "cover",
                  display: "block"
                }}
                onError={(e) => {
                  // Fallback if image doesn't exist
                  e.currentTarget.style.display = 'none';
                  const nextElement = e.currentTarget.nextElementSibling as HTMLElement;
                  if (nextElement) {
                    nextElement.style.display = 'flex';
                  }
                }}
              />
              {/* Fallback placeholder */}
              <Box
                sx={{
                  width: "100%",
                  height: { xs: "250px", md: "350px" },
                  display: "none",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "1.2rem",
                  fontWeight: "bold",
                  background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                  color: "white"
                }}
              >
                🏪 Maa Saryu Agency Showroom
              </Box>
              <Box
                sx={{
                  position: "absolute",
                  bottom: 0,
                  left: 0,
                  right: 0,
                  background: "linear-gradient(transparent, rgba(0,0,0,0.7))",
                  color: "white",
                  p: 3
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: "bold", mb: 1 }}>
                  Visit Our Showroom
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Experience our premium collection firsthand
                </Typography>
              </Box>
            </Paper>
          </Box>
        </Box>

        {/* Services Section */}
        <Box sx={{ mb: 8 }}>
          <Typography
            variant="h4"
            component="h3"
            sx={{
              textAlign: "center",
              fontWeight: "bold",
              color: "#1a237e",
              mb: 6
            }}
          >
            Our Services
          </Typography>
          <Box 
            sx={{ 
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" },
              gap: 4
            }}
          >
            {services.map((service, index) => (
              <Card
                key={index}
                sx={{
                  height: "100%",
                  borderRadius: "12px",
                  boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
                  transition: "all 0.3s ease-in-out",
                  "&:hover": {
                    transform: "translateY(-8px)",
                    boxShadow: "0 8px 30px rgba(0,0,0,0.15)",
                  }
                }}
              >
                <CardContent sx={{ textAlign: "center", p: 4 }}>
                  <service.icon
                    sx={{
                      fontSize: 48,
                      color: "#1a237e",
                      mb: 2
                    }}
                  />
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: "bold",
                      color: "#1a237e",
                      mb: 2
                    }}
                  >
                    {service.title}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "#666",
                      lineHeight: 1.6
                    }}
                  >
                    {service.description}
                  </Typography>
                </CardContent>
              </Card>
            ))}
          </Box>
        </Box>

        {/* Achievements Section */}
        <Box sx={{ mb: 8 }}>
          <Typography
            variant="h4"
            component="h3"
            sx={{
              textAlign: "center",
              fontWeight: "bold",
              color: "#1a237e",
              mb: 6
            }}
          >
            Why Choose Us
          </Typography>
          <Paper
            elevation={3}
            sx={{
              borderRadius: "16px",
              p: 4,
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              color: "white"
            }}
          >
            <Box 
              sx={{ 
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", md: "repeat(3, 1fr)" },
                gap: 3
              }}
            >
              {achievements.map((achievement, index) => (
                <Box key={index} sx={{ display: "flex", alignItems: "center" }}>
                  <CheckCircleIcon sx={{ color: "#4caf50", mr: 2, fontSize: 24 }} />
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {achievement}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Paper>
        </Box>

        {/* Contact Information */}
        <Box sx={{ textAlign: "center" }}>
          <Typography
            variant="h4"
            component="h3"
            sx={{
              fontWeight: "bold",
              color: "#1a237e",
              mb: 4
            }}
          >
            Visit Us Today
          </Typography>
          <Box 
            sx={{ 
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", md: "repeat(4, 1fr)" },
              gap: 4,
              justifyItems: "center"
            }}
          >
            <Box sx={{ textAlign: "center" }}>
              <LocationIcon sx={{ fontSize: 40, color: "#1a237e", mb: 1 }} />
              <Typography variant="h6" sx={{ fontWeight: "bold", mb: 1 }}>
                Location
              </Typography>
              <Typography variant="body2" sx={{ color: "#666" }}>
                Ashapur Rd, Darshan Nagar,<br />
                Ayodhya, UP 224135
              </Typography>
            </Box>
            <Box sx={{ textAlign: "center" }}>
              <PhoneIcon sx={{ fontSize: 40, color: "#1a237e", mb: 1 }} />
              <Typography variant="h6" sx={{ fontWeight: "bold", mb: 1 }}>
                Phone
              </Typography>
              <Typography variant="body2" sx={{ color: "#666" }}>
                +91 7897239423<br />
                +91 9651245787
              </Typography>
            </Box>
            <Box sx={{ textAlign: "center" }}>
              <EmailIcon sx={{ fontSize: 40, color: "#1a237e", mb: 1 }} />
              <Typography variant="h6" sx={{ fontWeight: "bold", mb: 1 }}>
                Email
              </Typography>
              <Typography variant="body2" sx={{ color: "#666" }}>
              info.maasaryuagency@gmail.com<br />
                sales.maasaryuagency@gmail.com
              </Typography>
            </Box>
            <Box sx={{ textAlign: "center" }}>
              <ScheduleIcon sx={{ fontSize: 40, color: "#1a237e", mb: 1 }} />
              <Typography variant="h6" sx={{ fontWeight: "bold", mb: 1 }}>
                Hours
              </Typography>
              <Typography variant="body2" sx={{ color: "#666" }}>
                Friday-Wednesday: 10AM-8PM<br />
                Thursday: Closed
              </Typography>
            </Box>
          </Box>
          <Box sx={{ mt: 6 }}>
            <Button
              variant="contained"
              size="large"
              sx={{
                backgroundColor: "#1a237e",
                px: 4,
                py: 1.5,
                borderRadius: "8px",
                fontSize: "1.1rem",
                fontWeight: "bold",
                "&:hover": {
                  backgroundColor: "#303f9f",
                }
              }}
              onClick={() => window.open('tel:+917897239423')}
            >
              Contact Us Today
            </Button>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default About;